import React, { useState, useEffect } from 'react';
import { FaTimes, FaFileInvoice, FaPaperPlane, FaPlus, FaTrash } from 'react-icons/fa';
import proformaService from '../../services/proformaService';
import bankService from '../../services/bankService';
import { parseFormattedNumber, formatNumberForInput } from '../../utils/numberFormat';
import './CreateProformaModal.css';

const GTIP_FIVE_AXIS = '8457.10.90.00.12';
const GTIP_THREE_AXIS = '8457.10.90.00.11';

const DEFAULT_EXTRA_NOTES = [
  'Çalışması için zorunlu olanlar dışında aksam ve aksesuar bulunmamaktadır.',
  'TRT BANDROL ÜCRETİNE TABİ CİHAZ YOKTUR.',
];

const CreateProformaModal = ({ offer, onClose, onProformaComplete }) => {
  const [price, setPrice] = useState('');
  const [gtipCode, setGtipCode] = useState(GTIP_FIVE_AXIS);
  const [gtipOption, setGtipOption] = useState('eu');
  const [terms, setTerms] = useState({
    deliveryTerms: 'EXW',
    paymentTerms: 'Antrepo devir öncesi peşin ödeme.',
    deliveryDate: 'Ödeme onayına istinaden.',
  });
  const [extraNotes, setExtraNotes] = useState(() => [...DEFAULT_EXTRA_NOTES]);
  const [newNoteInput, setNewNoteInput] = useState('');

  const [includeBankProforma, setIncludeBankProforma] = useState(false);
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [showNewBankForm, setShowNewBankForm] = useState(false);
  const [newBank, setNewBank] = useState({ companyName: '', address: '', taxOffice: '', taxNumber: '' });
  const [saveNewBank, setSaveNewBank] = useState(true);
  const [vatIncluded, setVatIncluded] = useState(false);
  const [ccEmails, setCcEmails] = useState([]);
  const [newCcEmail, setNewCcEmail] = useState('');

  const [isSending, setIsSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (offer?.price != null) {
      const p = parseFloat(offer.price);
      if (!isNaN(p)) setPrice(p.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      else setPrice(String(offer.price));
    }
  }, [offer]);

  useEffect(() => {
    if (!offer?.id) return;
    setGtipOption('eu');
    setGtipCode(GTIP_FIVE_AXIS);
    setTerms({
      deliveryTerms: 'EXW',
      paymentTerms: 'Antrepo devir öncesi peşin ödeme.',
      deliveryDate: 'Ödeme onayına istinaden.',
    });
    setExtraNotes([...DEFAULT_EXTRA_NOTES]);
    setIncludeBankProforma(false);
    setVatIncluded(false);
    setSelectedBankId('');
    setShowNewBankForm(false);
    setNewBank({ companyName: '', address: '', taxOffice: '', taxNumber: '' });
    setCcEmails([]);
    setNewCcEmail('');
    setError('');
  }, [offer?.id]);

  useEffect(() => {
    if (gtipOption === 'eu') {
      setGtipCode(GTIP_FIVE_AXIS);
    } else if (gtipOption === 'other') {
      setGtipCode(GTIP_THREE_AXIS);
    }
  }, [gtipOption]);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const data = await bankService.getBanks();
        setBanks(data);
      } catch (e) {
        console.error('Failed to fetch banks:', e);
      }
    };
    fetchBanks();
  }, []);

  const handlePriceChange = (e) => {
    setPrice(formatNumberForInput(e.target.value));
  };

  const buildTermsString = () => {
    return Object.entries(terms)
      .map(([, v], i) => {
        const keys = ['Teslimat Şartları', 'Ödeme Şartları', 'Teslimat Tarihi'];
        return `${keys[i]}:${v}`;
      })
      .join('|');
  };

  const handleAddNote = () => {
    const trimmed = newNoteInput.trim();
    if (trimmed) {
      setExtraNotes([...extraNotes, trimmed]);
      setNewNoteInput('');
    }
  };

  const handleRemoveNote = (idx) => {
    setExtraNotes(extraNotes.filter((_, i) => i !== idx));
  };

  const handleAddCcEmail = () => {
    if (newCcEmail.trim() && !ccEmails.includes(newCcEmail.trim())) {
      setCcEmails(prev => [...prev, newCcEmail.trim()]);
      setNewCcEmail('');
    }
  };

  const handleRemoveCcEmail = (emailToRemove) => {
    setCcEmails(prev => prev.filter(email => email !== emailToRemove));
  };

  const handleSendClick = () => {
    setError('');
    const numericPrice = parseFormattedNumber(price);
    if (!numericPrice || numericPrice <= 0) {
      setError('Geçerli bir fiyat giriniz.');
      return;
    }
    if (includeBankProforma && !selectedBankId && !showNewBankForm) {
      setError('Banka proforması için banka seçimi veya yeni banka girişi gereklidir.');
      return;
    }
    if (includeBankProforma && showNewBankForm && !newBank.companyName.trim()) {
      setError('Banka ünvanı zorunludur.');
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirm(false);
    setIsSending(true);
    setError('');

    try {
      const parsed = parseFormattedNumber(price);
      const numericPrice = typeof parsed === 'number' ? Math.round(parsed * 100) / 100 : 0;
      const requestData = {
        offerId: offer.id,
        price: numericPrice,
        gtipCode: gtipCode,
        terms: buildTermsString(),
        extraNotes: extraNotes.length > 0 ? extraNotes.join('\n') : null,
        vatIncluded,
        ccEmails,
      };

      if (includeBankProforma) {
        if (showNewBankForm && newBank.companyName.trim()) {
          requestData.newBank = newBank;
          requestData.saveNewBank = saveNewBank;
        } else if (selectedBankId) {
          requestData.bankId = parseInt(selectedBankId);
        }
      }

      const result = await proformaService.createProforma(requestData);
      setSuccessInfo(result || null);
      setShowSuccess(true);
      setTimeout(() => {
        if (onProformaComplete) onProformaComplete();
      }, 2000);
    } catch (e) {
      setError(e.message || 'Proforma gönderilirken bir hata oluştu.');
    } finally {
      setIsSending(false);
    }
  };

  if (!offer) return null;

  return (
    <div className="create-proforma-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="create-proforma-modal">
        <div className="modal-header">
          <h2><FaFileInvoice style={{ color: '#3b82f6' }} /> Proforma Gönder</h2>
          <button className="close-button" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="modal-body">
          {/* Offer Info */}
          <div className="proforma-section">
            <h3>Teklif Bilgileri</h3>
            <div className="proforma-info-grid">
              <div className="proforma-info-item">
                <span className="label">Proje Kodu:</span>
                <span className="value">{offer.projectCode || '-'}</span>
              </div>
              <div className="proforma-info-item">
                <span className="label">Müşteri:</span>
                <span className="value">{offer.clientCompanyName || '-'}</span>
              </div>
              <div className="proforma-info-item">
                <span className="label">Teklif Fiyatı:</span>
                <span className="value">{offer.price ? `${parseFloat(offer.price).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-'}</span>
              </div>
              <div className="proforma-info-item">
                <span className="label">Tarih:</span>
                <span className="value">{offer.sentAt ? new Date(offer.sentAt).toLocaleDateString('tr-TR') : '-'}</span>
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="proforma-form-group">
            <label>Proforma Fiyatı (EUR)</label>
            <input
              type="text"
              value={price}
              onChange={handlePriceChange}
              placeholder="Fiyat giriniz"
            />
          </div>

          {/* CC Email Section */}
          <div className="proforma-form-group">
            <label>CC E-postalar</label>
            <div className="cc-email-container">
              <div className="cc-email-input-row">
                <input
                  type="email"
                  value={newCcEmail}
                  onChange={(e) => setNewCcEmail(e.target.value)}
                  placeholder="CC e-posta adresi girin. (Eklemek istediğiniz mailleri + tuşuna basarak ekleyebilirsiniz)"
                  className="cc-email-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCcEmail();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddCcEmail}
                  className="btn-add-cc"
                  disabled={!newCcEmail.trim()}
                >
                  <FaPlus />
                </button>
              </div>

              {ccEmails.length > 0 && (
                <div className="cc-email-list">
                  {ccEmails.map((email, index) => (
                    <div key={index} className="cc-email-item">
                      <span className="cc-email-text">{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCcEmail(email)}
                        className="btn-remove-cc"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* GTIP */}
          <div className="proforma-form-group">
            <label>GTİP Kodu</label>
            <div className="gtip-radio-group">
              <label>
                <input type="radio" name="gtip" value="eu" checked={gtipOption === 'eu'} onChange={() => setGtipOption('eu')} />
                5 eksen ({GTIP_FIVE_AXIS})
              </label>
              <label>
                <input type="radio" name="gtip" value="other" checked={gtipOption === 'other'} onChange={() => setGtipOption('other')} />
                3 eksen ({GTIP_THREE_AXIS})
              </label>
              <label>
                <input type="radio" name="gtip" value="custom" checked={gtipOption === 'custom'} onChange={() => setGtipOption('custom')} />
                Özel giriş
              </label>
            </div>
            {gtipOption === 'custom' && (
              <input
                type="text"
                value={gtipCode}
                onChange={(e) => setGtipCode(e.target.value)}
                placeholder="Örn: 8457.10.90.00.11"
                style={{ marginTop: '8px', padding: '8px 12px', width: '100%', maxWidth: '280px' }}
              />
            )}
          </div>

          {/* Terms */}
          <div className="proforma-section">
            <h3>Şartlar</h3>
            <div className="proforma-form-group">
              <label>Teslimat Şartları</label>
              <input type="text" value={terms.deliveryTerms} onChange={(e) => setTerms({ ...terms, deliveryTerms: e.target.value })} />
            </div>
            <div className="proforma-form-group">
              <label>Ödeme Şartları</label>
              <input type="text" value={terms.paymentTerms} onChange={(e) => setTerms({ ...terms, paymentTerms: e.target.value })} />
            </div>
            <div className="proforma-form-group">
              <label>Teslimat Tarihi</label>
              <input type="text" value={terms.deliveryDate} onChange={(e) => setTerms({ ...terms, deliveryDate: e.target.value })} />
            </div>
          </div>

          {/* Ek Notlar */}
          <div className="proforma-section">
            <h3>Ek Notlar</h3>
            <p className="proforma-hint" style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Varsayılan iki madde aşağıdadır; istemezseniz çöp kutusu ile kaldırabilirsiniz. İsterseniz yeni madde ekleyin.
            </p>
            <div className="extra-notes-list">
              {extraNotes.map((note, idx) => (
                <div key={idx} className="extra-note-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ flex: 1, padding: '6px 10px', background: '#f8f9fa', borderRadius: '6px', fontSize: '13px' }}>{note}</span>
                  <button type="button" onClick={() => handleRemoveNote(idx)} className="btn-remove-note" title="Kaldır" style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer' }}>
                    <FaTrash size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className="add-note-row" style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <input
                type="text"
                value={newNoteInput}
                onChange={(e) => setNewNoteInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddNote())}
                placeholder="Yeni madde ekleyin..."
                style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
              <button type="button" onClick={handleAddNote} className="btn-add-note" style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontWeight: 600 }}>
                <FaPlus size={14} /> Ekle
              </button>
            </div>
          </div>

          {/* Bank Section - optional, controlled by switch */}
          <div className="bank-section">
            <div className="bank-proforma-switch-row">
              <label className="switch-label">
                <input
                  type="checkbox"
                  checked={includeBankProforma}
                  onChange={(e) => setIncludeBankProforma(e.target.checked)}
                />
                <span className="switch-slider"></span>
                <span className="switch-text">Banka proforması da oluştur</span>
              </label>
            </div>
            <div className="bank-proforma-switch-row" style={{ marginTop: '10px' }}>
              <label className="switch-label">
                <input
                  type="checkbox"
                  checked={vatIncluded}
                  onChange={(e) => setVatIncluded(e.target.checked)}
                />
                <span className="switch-slider"></span>
                <span className="switch-text">KDV dahil (%20 satırı ve toplam)</span>
              </label>
            </div>
            {includeBankProforma && (
              <>
            <div className="proforma-form-group">
              <label>Banka Seçimi <span style={{ color: '#dc2626' }}>*</span></label>
              <select
                value={showNewBankForm ? 'new' : selectedBankId}
                onChange={(e) => {
                  if (e.target.value === 'new') {
                    setShowNewBankForm(true);
                    setSelectedBankId('');
                  } else {
                    setShowNewBankForm(false);
                    setSelectedBankId(e.target.value);
                  }
                }}
              >
                <option value="">Banka seçiniz...</option>
                {banks.map(b => (
                  <option key={b.id} value={b.id}>{b.companyName}</option>
                ))}
                <option value="new">+ Yeni Banka Ekle</option>
              </select>
            </div>

            {showNewBankForm && (
              <div className="new-bank-form">
                <div className="proforma-form-group">
                  <label>Banka Ünvanı *</label>
                  <input type="text" value={newBank.companyName} onChange={(e) => setNewBank({ ...newBank, companyName: e.target.value })} placeholder="Banka ünvanı" />
                </div>
                <div className="proforma-form-group">
                  <label>Adres</label>
                  <input type="text" value={newBank.address} onChange={(e) => setNewBank({ ...newBank, address: e.target.value })} placeholder="Banka adresi" />
                </div>
                <div className="proforma-form-group">
                  <label>Vergi Dairesi</label>
                  <input type="text" value={newBank.taxOffice} onChange={(e) => setNewBank({ ...newBank, taxOffice: e.target.value })} placeholder="Vergi dairesi" />
                </div>
                <div className="proforma-form-group">
                  <label>Vergi No</label>
                  <input type="text" value={newBank.taxNumber} onChange={(e) => setNewBank({ ...newBank, taxNumber: e.target.value })} placeholder="Vergi numarası" />
                </div>
                <div className="save-bank-checkbox">
                  <input type="checkbox" id="saveBankCheck" checked={saveNewBank} onChange={(e) => setSaveNewBank(e.target.checked)} />
                  <label htmlFor="saveBankCheck">Bu bankayı kayıtlı bankalara ekle</label>
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <p style={{ color: '#dc2626', margin: 0, fontSize: '0.9rem' }}>{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="proforma-actions">
            <button className="cancel-btn" onClick={onClose} disabled={isSending}>İptal</button>
            <button className="submit-btn" onClick={handleSendClick} disabled={isSending}>
              {isSending ? <><span className="loading-spinner-small"></span> Gönderiliyor...</> : <><FaPaperPlane /> Proforma Gönder</>}
            </button>
          </div>
        </div>

        {/* Confirmation */}
        {showConfirm && (
          <div className="confirm-overlay">
            <div className="confirm-box">
              <h3>Proforma Gönderilsin mi?</h3>
              <p>
                <strong>{offer.clientCompanyName}</strong> müşterisine proforma fatura gönderilecektir.
                {includeBankProforma && (selectedBankId || showNewBankForm) && ' Ayrıca banka proforması da oluşturulacaktır.'}
              </p>
              <div className="confirm-buttons">
                <button className="confirm-no" onClick={() => setShowConfirm(false)}>Vazgeç</button>
                <button className="confirm-yes" onClick={handleConfirmSend}>Onayla ve Gönder</button>
              </div>
            </div>
          </div>
        )}

        {/* Success */}
        {showSuccess && (
          <div className="success-overlay">
            <div className="success-box">
              <div className="success-icon"><FaFileInvoice /></div>
              <h3>Proforma Gönderildi!</h3>
              <p>Proforma fatura başarıyla oluşturuldu.</p>
              {successInfo?.emailSent === false ? (
                <p style={{ color: '#b45309', fontWeight: 600 }}>
                  E-posta gönderilemedi: {successInfo.emailError || 'Bilinmeyen hata'}
                </p>
              ) : (
                <p>Proforma müşteriye e-posta ile gönderildi.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateProformaModal;
